import "./contacts.scss";

import PageTitleBreadcrumb from "@frontend/js/Components/Shared/PageTitleBreadcrumb/PageTitleBreadcrumb";
import ContactMap from "@frontend/js/Components/Site/ContactMap/ContactMap";
import MetaHead from "@frontend/js/Components/Shared/MetaHead/MetaHead";
import LocationIcon from "@frontend/images/icons/LocationIcon";
import Form from "@frontend/js/Components/Shared/Form/Form";
import SiteLayout from "@frontend/js/Layouts/SiteLayout";
import ShareIcon from "@frontend/images/icons/ShareIcon";
import PhoneIcon from "@frontend/images/icons/PhoneIcon";
import MailIcon from "@frontend/images/icons/MailIcon";
import useT from "@frontend/js/hooks/useT";
import { usePage } from "@inertiajs/react";

export default function Contacts({ page, meta, mapData }) {
    const { t } = useT("site");
    const pageProps = usePage().props;
    const locale = pageProps?.currentLocale || "ro";
    const title = page?.title?.[locale] || "";

    const {
        companyName,
        address,
        locationCode,
        mapAddress,
        mapCoordinates,
        mapLink,
        phone,
        email,
        administrationPhone,
        vhrCareersPhone,
        facebook,
        instagram,
        twitter,
        linkedin,
    } = mapData || {};

    async function handleSubmit(
        formData,
        { reset, clearErrors, setError, source, sourceId },
    ) {
        try {
            const csrfToken = document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content");

            const response = await fetch("/contact-form", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-TOKEN": csrfToken || "",
                },
                body: JSON.stringify({
                    ...formData,
                    locale,
                    source: source || 1,
                    ...(sourceId && { sourceId }),
                }),
            });

            const text = await response.text();

            if (response.status === 422) {
                const result = JSON.parse(text);
                if (result.errors) {
                    Object.entries(result.errors).forEach(
                        ([field, messages]) => {
                            setError(field, messages[0]);
                        },
                    );
                }
                return { validationErrors: true };
            }

            if (!response.ok) {
                console.error("Form submission error:", text);
                return false;
            }

            const result = JSON.parse(text);

            if (!result.success) {
                return false;
            }

            reset();
            return true;
        } catch (error) {
            console.error("Form submission error:", error);
            return false;
        }
    }

    return (
        <>
            <MetaHead meta={meta} />

            <SiteLayout>
                <div className="contacts page-shell">
                    <PageTitleBreadcrumb title={title} />
                    <div className="contacts__info">
                        <div className="contacts__info__form">
                            <h2>{t("contact-form-title")}</h2>
                            <Form onSubmit={handleSubmit} source={1} />
                        </div>
                        <div className="contacts__info__cards">
                            <div className="contacts__info__cards__card">
                                <LocationIcon />
                                <h4>{t("contact-visit-us")}</h4>
                                <a
                                    className="contacts__info__cards__card__address"
                                    href={mapLink || "#"}
                                    target={mapLink ? "_blank" : undefined}
                                    rel={
                                        mapLink
                                            ? "noopener noreferrer"
                                            : undefined
                                    }
                                >
                                    {mapAddress || address}
                                </a>
                            </div>
                            <div className="contacts__info__cards__card">
                                <MailIcon />
                                <h4>{t("contact-email-label")}</h4>
                                <a href={`mailto:${email}`}>{email}</a>
                            </div>
                            <div className="contacts__info__cards__card">
                                <PhoneIcon />
                                <h4>{t("contact-administration")}</h4>
                                <a href={`tel:${administrationPhone}`}>
                                    {administrationPhone}
                                </a>
                            </div>
                            <div className="contacts__info__cards__card">
                                <PhoneIcon />
                                <h4>{t("contact-vhr-careers")}</h4>
                                <a href={`tel:${vhrCareersPhone}`}>
                                    {vhrCareersPhone}
                                </a>
                            </div>
                            <div className="contacts__info__cards__card">
                                <ShareIcon />
                                <h4>{t("contact-follow-us")}</h4>
                                <div className="contacts__info__cards__card__links">
                                    <a href={facebook}>{t("facebook-label")}</a>
                                    <a href={instagram}>
                                        {t("instagram-label")}
                                    </a>
                                    <a href={twitter}>{t("twitter-label")}</a>
                                    <a href={linkedin}>{t("linkedin-label")}</a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <ContactMap
                        companyName={companyName}
                        address={address}
                        mapAddress={mapAddress || address}
                        mapCoordinates={mapCoordinates}
                        mapLink={mapLink}
                    />
                </div>
            </SiteLayout>
        </>
    );
}
